import { useRequest } from 'ahooks';
import { Popover, Rate, Space, Spin } from 'antd';
import { createContext, useContext, useState } from 'react';
import {
  BaseDataAnalyzeDTO,
  ChannelAnalyzeRequest,
  CustomerStateEnum,
  dataAnalyzeController,
  Long,
} from '../api';
import { useCompany } from '../hooks/company';

interface StarAnalyzeContextValue {
  request: ChannelAnalyzeRequest | null;
}

const StarAnalyzeContext = createContext<StarAnalyzeContextValue | null>(null);

export const StarAnalyzeProvider = StarAnalyzeContext.Provider;

export const useStarAnalyzeContext = () => {
  const context = useContext(StarAnalyzeContext);
  if (!context) {
    throw new Error(
      'useStarAnalyzeContext must be used within StarAnalyzeProvider',
    );
  }
  return context;
};

interface StarAnalyzeNumberProps {
  value: Long;
  state: CustomerStateEnum;
  channel: string;
}

const StarDistribution = ({ data }: { data: BaseDataAnalyzeDTO }) => {
  const star0 = Number(data.star0);
  const star1 = Number(data.star1);
  const star2 = Number(data.star2);
  const star3 = Number(data.star3);
  const star4 = Number(data.star4);
  const star5 = Number(data.star5);

  const total = star0 + star1 + star2 + star3 + star4 + star5;

  if (total === 0) {
    return <div style={{ padding: '8px' }}>暂无数据</div>;
  }

  const starItems = [
    { star: 5, count: star5 },
    { star: 4, count: star4 },
    { star: 3, count: star3 },
    { star: 2, count: star2 },
    { star: 1, count: star1 },
    { star: 0, count: star0 },
  ];

  return (
    <div style={{ padding: '8px', minWidth: '200px' }}>
      <Space direction="vertical" style={{ width: '100%' }}>
        {starItems.map(({ star, count }) => (
          <div
            key={star}
            style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
            }}
          >
            <Rate
              disabled
              value={star}
              count={star || 1}
              style={{ fontSize: '12px' }}
            />
            <Space>
              <span>{count}</span>
              <span style={{ color: '#999', fontSize: '12px' }}>
                ({((count * 100) / total).toFixed(1)}%)
              </span>
            </Space>
          </div>
        ))}
      </Space>
    </div>
  );
};

export default function StarAnalyzeNumber({
  value,
  state,
  channel,
}: StarAnalyzeNumberProps) {
  const { id: companyId } = useCompany();
  const { request } = useStarAnalyzeContext();
  const [open, setOpen] = useState(false);

  const { data, loading, run } = useRequest(
    async () => {
      return dataAnalyzeController.channelStarAnalyze(
        companyId,
        request,
        state,
        channel,
      );
    },
    {
      manual: true,
    },
  );

  const handleOpenChange = (visible: boolean) => {
    setOpen(visible);
    if (visible) {
      run();
    }
  };

  // 如果 value 是 0、null 或 undefined，直接显示数字，不显示 popover
  if (!value) {
    return <span>{value || 0}</span>;
  }

  return (
    <Popover
      content={
        loading ? (
          <div style={{ padding: '8px' }}>
            <Spin size="small" />
          </div>
        ) : data ? (
          <StarDistribution data={data} />
        ) : null
      }
      title="星级分布"
      trigger="hover"
      open={open}
      onOpenChange={handleOpenChange}
    >
      <Spin spinning={loading} size="small">
        <span style={{ cursor: 'pointer' }}>{value}</span>
      </Spin>
    </Popover>
  );
}
